// @flow
import { Map } from "immutable"
import { createSelector } from "reselect"
import { groupBy, toPairs } from "ramda"
import { startOfMonth, compareAsc } from "date-fns"
import type { InputSelector } from "reselect"

import urlParserSelector from "common/services/url/parserSelector"
import {
  createPlace,
  createSearchForm,
  defaultSearchFormSelector,
} from "common/services/searchForm/searchFormSelectors"
import { searchModesSelector } from "common/services/branding/selectors"
import { filtersFromPlainJS } from "common/records/FiltersState"
import { getItem } from "common/browser/utils/localStorage"
import { STARRED } from "common/consts/localStorageKeys"
import * as starredUtils from "common/records/Starred"
import { getUrl, getJourneyUrl } from "common/services/url/urlBuilders"
import filtersToQuery from "common/services/filters/filtersToQuery"
import formatterSelector from "common/services/formatter/selector"
import getPathSelector from "common/services/getPath/selector"
import searchFormToQueryPart from "common/services/search/searchFormToQueryPart"
import searchFormToUrl from "common/services/search/searchFormToUrl"
import SearchFormState from "common/records/SearchFormState"
import type { StarredItem, StarredPlaces } from "common/records/Starred.types"
import type { StoreState } from "common/services/types"
import type Place from "common/records/Place"

const starredStoredDataSelector = () => getItem(STARRED)
const optionsSelector = state => state.options
const placesSelector = state => state.places && state.places.get("places")

export const starredSelector = (state: any) => state.starred

export const starredItemsSelector: $FlowFixMe = createSelector(
  [starredSelector],
  starred => starred.items,
)

export const starredItemsByMonthSelectors: $FlowFixMe = createSelector(
  [starredItemsSelector],
  starredItems =>
    toPairs(
      groupBy(
        item =>
          startOfMonth(
            item.journey
              .getDeparture()
              .getIn(["when", "utc"])
              .toDate(),
          ).toJSON(),
        starredItems,
      ),
    )
      .map(([month, items]) => ({ month, items }))
      .sort((a, b) => compareAsc(a.month, b.month)),
)

const starredStoredRawSelector: $FlowFixMe = createSelector(
  [starredStoredDataSelector],
  starredStoredData => {
    if (!starredStoredData) {
      return []
    }

    try {
      return JSON.parse(starredStoredData)
    } catch (err) {
      return []
    }
  },
)

export const starredStoredSelector: $FlowFixMe = createSelector(
  [starredStoredRawSelector],
  starredStoredRaw => starredUtils.storeFromPlainJS({ items: starredStoredRaw }),
)

export const starredPlacesSelector: InputSelector<StoreState, any, StarredPlaces> = createSelector(
  [starredSelector, placesSelector],
  (starred, places) => (places ? { ...starred.places, ...places.toObject() } : starred.places),
)

const starredPlacesMapSelector: InputSelector<StoreState, any, Map<string, Place>> = createSelector(
  [starredPlacesSelector],
  starredPlaces => Map(starredPlaces),
)

// eslint-disable-next-line flowtype/generic-spacing
export const starredItemBaseUrlGetterSelector: InputSelector<
  StoreState,
  any,
  (item: StarredItem) => string,
> = createSelector(
  [
    starredPlacesMapSelector,
    defaultSearchFormSelector,
    urlParserSelector,
    optionsSelector,
    formatterSelector,
    getPathSelector,
    searchModesSelector,
  ],
  (places, defaultSearchForm, urlParser, options, formatter, getPath, modesEnabled) => (
    item: StarredItem,
  ): string => {
    const { multicity } = item.form
    const { salesman } = item.form
    const origin = createPlace("origin")(item.form.origin, places)
    const destination = createPlace("destination")(item.form.destination, places)
    const { outboundDate } = item.form
    const { inboundDate } = item.form
    const { passengers } = item.form
    const { cabinClass } = item.form

    const baseSearchForm = defaultSearchForm || new SearchFormState()

    const searchForm = createSearchForm(
      origin,
      destination,
      outboundDate,
      inboundDate,
      multicity,
      salesman,
      passengers,
      cabinClass,
      baseSearchForm,
      urlParser,
      places,
      modesEnabled,
    )
    if (item.form.starType === "search" && item.form.filters) {
      const filters = filtersFromPlainJS(item.form.filters)

      const urlParts = {
        query: {
          ...filtersToQuery(filters, searchForm, options),
          ...searchFormToQueryPart(searchForm, baseSearchForm, formatter),
        },
        pathname: searchFormToUrl(searchForm, baseSearchForm, formatter, getPath, options),
      }

      return getUrl(urlParts)
    }
    return item.form.returnUrl
  },
)

// eslint-disable-next-line flowtype/generic-spacing
export const starredItemUrlSelector: InputSelector<
  StoreState,
  any,
  (item: StarredItem) => string,
> = createSelector(
  [starredItemBaseUrlGetterSelector],
  starredItemBaseUrlGetter => (item: StarredItem) =>
    getJourneyUrl(starredItemBaseUrlGetter(item), item.journey),
)
